import { Link } from "wouter";
import { ArrowLeft } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import PageHero from "@/components/PageHero";
import { IMAGES } from "@/lib/constants";

export default function CaseForsvar() {
  return (
    <Layout>
      <SEOHead
        title="Försvarsindustrin - Säkerhetskritiska komponenter | ASPAB"
        description="ASPAB tillverkar säkerhetskritiska komponenter till försvarssektorn. Specialmaterial, ballistiska krav och pågående ISO-certifiering."
        canonical="https://aspab.com/referenser/forsvarsindustrin"
        path="/referenser/forsvarsindustrin"
        breadcrumbs={[
          { name: "Referenser", href: "/referenser" },
          { name: "Försvarsindustrin", href: "/referenser/forsvarsindustrin" },
        ]}
      
        ogImage="/images/og-case-forsvar_03bbf707.png"
      />

      <PageHero
        title="Försvarsindustrin"
        subtitle="Säkerhetskritiska komponenter med extrema krav på material och kvalitet. Vår pågående ISO-certifiering öppnar dörrar till denna krävande sektor."
        breadcrumbs={[
          { name: "Referenser", href: "/referenser" },
          { name: "Försvarsindustrin", href: "/referenser/forsvarsindustrin" },
        ]}
        image={IMAGES.workshop2}
      />

      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <AnimatedSection>
              <div className="flex flex-wrap gap-2 mb-8">
                {["Försvar", "Specialmaterial", "Ballistik", "ISO-certifiering"].map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-[#2563EB]/10 text-[#2563EB] text-xs font-medium rounded-lg">
                    {tag}
                  </span>
                ))}
              </div>

              <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">Bakgrund</h2>
              <p className="text-gray-500 mt-4 leading-relaxed">
                ASPAB tillverkar komponenter till skjutbanor och andra tillämpningar inom 
                försvarssektorn. Det är projekt där säkerhet och prestanda är kritiska faktorer, 
                och där fel kan få allvarliga konsekvenser.
              </p>
            </AnimatedSection>

            <AnimatedSection delay={100}>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mt-12">Utmaningen</h2>
              <p className="text-gray-500 mt-4 leading-relaxed">
                Försvarssektorn ställer extrema krav på materialprestanda, inklusive ballistisk 
                motståndskraft. Komponenterna måste tåla väder och vind, korrosion och mekanisk 
                påfrestning. Dessutom kräver branschen certifieringar och dokumentation som 
                säkerställer spårbarhet och kvalitet.
              </p>

              <blockquote className="mt-8 pl-5 border-l-2 border-[#2563EB]/50">
                <p className="text-gray-500 italic leading-relaxed">
                  "Då pratar vi om att jag skulle klara ballistik och då behöver man ju kolla 
                  på någonting annat än svart stål. Ska den sitta under tid och bli påverkad av 
                  väder vind, spelar det någon roll?"
                </p>
                <cite className="text-[#2563EB] text-sm mt-2 block not-italic">Robin Fischer, ASPAB</cite>
              </blockquote>
            </AnimatedSection>

            <AnimatedSection delay={150}>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mt-12">Vår lösning</h2>
              <p className="text-gray-500 mt-4 leading-relaxed">
                Vi använder vår djupa materialkunskap för att välja rätt specialstål för 
                ballistiska tillämpningar. Rådgivning om ytbehandling för väderbeständighet 
                är en central del av processen.
              </p>
              <p className="text-gray-500 mt-4 leading-relaxed">
                Kvalitetssäkringen är extra noggrann för säkerhetskritiska komponenter, med 
                dokumentation enligt försvarsstandarder. Vår pågående ISO-certifiering (9001, 
                14001, 45001) är ett viktigt steg för att möta branschens krav.
              </p>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mt-12">Resultat</h2>
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                {[
                  { title: "Nöjda kunder", desc: "Etablerad position inom säkerhetskritiska tillämpningar." },
                  { title: "Materialexpertis", desc: "Djup kunskap om specialstål och ballistiska krav." },
                  { title: "Grund för expansion", desc: "ISO-certifiering öppnar dörrar till fler försvarsprojekt." },
                  { title: "Kvalitetssystem", desc: "Dokumenterade processer som möter försvarsstandarder." },
                ].map((r) => (
                  <div key={r.title} className="bg-white border border-gray-200 rounded-lg p-5">
                    <h3 className="text-gray-900 font-semibold">{r.title}</h3>
                    <p className="text-gray-500 text-sm mt-1">{r.desc}</p>
                  </div>
                ))}
              </div>
            </AnimatedSection>

            <AnimatedSection delay={250}>
              <div className="mt-16 pt-8 border-t border-gray-200 flex justify-between">
                <Link href="/referenser/volvo" className="flex items-center gap-2 text-gray-500 hover:text-[#2563EB] transition-colors">
                  <ArrowLeft className="w-4 h-4" /> Volvo
                </Link>
                <Link href="/referenser" className="flex items-center gap-2 text-gray-500 hover:text-[#2563EB] transition-colors">
                  Alla referenser
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </Layout>
  );
}
